import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, TextField, Button,
  Grid, Divider, Alert, Card, CardContent, InputAdornment,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Sms as SmsIcon,
  Email as EmailIcon,
  Chat as LineIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const NotificationSettings = () => {
  const { t } = useTranslation('admin');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // 通知設定
  const [smsSettings, setSmsSettings] = useState({
    provider: 'mitake',
    username: '',
    password: '',
    sender_name: ''
  });

  const [emailSettings, setEmailSettings] = useState({
    provider: 'aws_ses',
    region: 'ap-northeast-1',
    access_key: '',
    secret_key: '',
    sender_email: '',
    sender_name: '優爾商城'
  });

  const [lineSettings, setLineSettings] = useState({
    channel_access_token: '',
    channel_secret: '',
    webhook_url: ''
  });

  // 測試對話框
  const [testDialog, setTestDialog] = useState({
    open: false,
    type: '', // sms, email, line
    data: {}
  });

  // 通知日誌
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // 追蹤日誌
  const [trackingLogs, setTrackingLogs] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 3) {
      loadNotificationLogs();
    } else if (activeTab === 4) {
      loadTrackingLogs();
    }
  }, [activeTab]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/notifications');
      if (response.data.success) {
        const { sms, email, line } = response.data.settings;
        setSmsSettings(prev => ({ ...prev, ...sms }));
        setEmailSettings(prev => ({ ...prev, ...email }));
        setLineSettings(prev => ({ ...prev, ...line }));
      }
    } catch (error) {
      console.error('Load settings failed:', error);
      toast.error(t('notifications.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await api.put('/settings/notifications', {
        sms: smsSettings,
        email: emailSettings,
        line: lineSettings
      });

      if (response.data.success) {
        toast.success(t('notifications.saved'));
      } else {
        toast.error(t('notifications.save_failed'));
      }
    } catch (error) {
      console.error('Save settings failed:', error);
      toast.error(t('notifications.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const openTestDialog = (type) => {
    let initialData = {};
    switch (type) {
      case 'sms':
        initialData = { phone: '', message: t('notifications.test_sms_message') };
        break;
      case 'email':
        initialData = { to: '', subject: t('notifications.test_email_subject'), htmlContent: `<p>${t('notifications.test_email_body')}</p>` };
        break;
      case 'line':
        initialData = { message: t('notifications.test_line_message') };
        break;
    }
    setTestDialog({ open: true, type, data: initialData });
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      let response;
      const { type, data } = testDialog;

      switch (type) {
        case 'sms':
          response = await api.post('/settings/test-sms', data);
          break;
        case 'email':
          response = await api.post('/settings/test-email', data);
          break;
        case 'line':
          response = await api.post('/settings/test-line', data);
          break;
      }

      if (response.data.success) {
        toast.success(t('notifications.send_success', { type: getTypeLabel(type) }));
        setTestDialog({ open: false, type: '', data: {} });
        if (activeTab === 3) {
          loadNotificationLogs();
        }
      } else {
        toast.error(response.data.error || t('notifications.send_failed'));
      }
    } catch (error) {
      console.error('Test send failed:', error);
      toast.error(error.response?.data?.error || t('notifications.test_send_failed'));
    } finally {
      setTesting(false);
    }
  };

  const loadNotificationLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await api.get('/settings/notification-logs?limit=50');
      if (response.data.success) {
        setNotificationLogs(response.data.logs);
      }
    } catch (error) {
      console.error('Load notification logs failed:', error);
      toast.error(t('notifications.load_logs_failed'));
    } finally {
      setLogsLoading(false);
    }
  };

  const loadTrackingLogs = async () => {
    setTrackingLoading(true);
    try {
      const response = await api.get('/settings/tracking-logs?limit=50');
      if (response.data.success) {
        setTrackingLogs(response.data.logs);
      }
    } catch (error) {
      console.error('Load tracking logs failed:', error);
      toast.error(t('notifications.load_tracking_failed'));
    } finally {
      setTrackingLoading(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = { sms: t('notifications.type_sms'), email: t('notifications.type_email'), line: t('notifications.type_line') };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      sent: 'success',
      pending: 'warning',
      failed: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      sent: t('notifications.status_sent'),
      pending: t('notifications.status_pending'),
      failed: t('notifications.status_failed')
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('notifications.title')}</Typography>
        {activeTab < 3 && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveSettings}
            disabled={saving}
          >
            {saving ? t('notifications.saving') : t('notifications.save_settings')}
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label={t('notifications.tab_sms')} icon={<SmsIcon />} iconPosition="start" />
          <Tab label={t('notifications.tab_email')} icon={<EmailIcon />} iconPosition="start" />
          <Tab label={t('notifications.tab_line')} icon={<LineIcon />} iconPosition="start" />
          <Tab label={t('notifications.tab_notification_logs')} icon={<HistoryIcon />} iconPosition="start" />
          <Tab label={t('notifications.tab_tracking_logs')} icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* 簡訊設定 */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('notifications.mitake_sms_settings')}</Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('notifications.provider')}
                  value={smsSettings.provider}
                  disabled
                  helperText={t('notifications.mitake_only')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('notifications.sender_name')}
                  value={smsSettings.sender_name}
                  onChange={(e) => setSmsSettings({ ...smsSettings, sender_name: e.target.value })}
                  placeholder="URStore"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('notifications.username')}
                  value={smsSettings.username}
                  onChange={(e) => setSmsSettings({ ...smsSettings, username: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label={t('notifications.password')}
                  value={smsSettings.password}
                  onChange={(e) => setSmsSettings({ ...smsSettings, password: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  {t('notifications.mitake_signup_hint')}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => openTestDialog('sms')}
                >
                  {t('notifications.test_sms')}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* 郵件設定 */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('notifications.aws_ses_settings')}</Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('notifications.provider')}
                  value={emailSettings.provider}
                  disabled
                  helperText={t('notifications.aws_ses_only')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="AWS Region"
                  value={emailSettings.region}
                  onChange={(e) => setEmailSettings({ ...emailSettings, region: e.target.value })}
                  placeholder="ap-northeast-1"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Access Key"
                  value={emailSettings.access_key}
                  onChange={(e) => setEmailSettings({ ...emailSettings, access_key: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Secret Key"
                  value={emailSettings.secret_key}
                  onChange={(e) => setEmailSettings({ ...emailSettings, secret_key: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('notifications.sender_email')}
                  type="email"
                  value={emailSettings.sender_email}
                  onChange={(e) => setEmailSettings({ ...emailSettings, sender_email: e.target.value })}
                  placeholder="noreply@example.com"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('notifications.sender_name_label')}
                  value={emailSettings.sender_name}
                  onChange={(e) => setEmailSettings({ ...emailSettings, sender_name: e.target.value })}
                  placeholder="優爾商城"
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  {t('notifications.aws_ses_verify_hint')}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => openTestDialog('email')}
                >
                  {t('notifications.test_email')}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* LINE 設定 */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('notifications.line_notify_settings')}</Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Channel Access Token"
                  value={lineSettings.channel_access_token}
                  onChange={(e) => setLineSettings({ ...lineSettings, channel_access_token: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Channel Secret"
                  value={lineSettings.channel_secret}
                  onChange={(e) => setLineSettings({ ...lineSettings, channel_secret: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={lineSettings.webhook_url}
                  onChange={(e) => setLineSettings({ ...lineSettings, webhook_url: e.target.value })}
                  placeholder="https://your-domain.com/api/line/webhook"
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  {t('notifications.line_signup_hint')}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => openTestDialog('line')}
                >
                  {t('notifications.test_line')}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* 通知日誌 */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('notifications.notification_logs_title')}</Typography>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={loadNotificationLogs}
                disabled={logsLoading}
              >
                {t('notifications.refresh')}
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {logsLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('notifications.col_type')}</TableCell>
                      <TableCell>{t('notifications.col_recipient')}</TableCell>
                      <TableCell>{t('notifications.col_subject')}</TableCell>
                      <TableCell>{t('notifications.col_status')}</TableCell>
                      <TableCell>{t('notifications.col_order_id')}</TableCell>
                      <TableCell>{t('notifications.col_sent_time')}</TableCell>
                      <TableCell>{t('notifications.col_error')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notificationLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          {t('notifications.no_notification_logs')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      notificationLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Chip
                              size="small"
                              label={getTypeLabel(log.notification_type)}
                              icon={
                                log.notification_type === 'sms' ? <SmsIcon /> :
                                log.notification_type === 'email' ? <EmailIcon /> :
                                <LineIcon />
                              }
                            />
                          </TableCell>
                          <TableCell>{log.recipient}</TableCell>
                          <TableCell>
                            {log.subject || log.content?.substring(0, 50)}
                            {log.content?.length > 50 && '...'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={getStatusLabel(log.status)}
                              color={getStatusColor(log.status)}
                              icon={
                                log.status === 'sent' ? <SuccessIcon /> :
                                log.status === 'failed' ? <ErrorIcon /> :
                                null
                              }
                            />
                          </TableCell>
                          <TableCell>{log.order_id || '-'}</TableCell>
                          <TableCell>
                            {new Date(log.created_at).toLocaleString('zh-TW')}
                          </TableCell>
                          <TableCell>
                            {log.error_message && (
                              <Typography variant="caption" color="error">
                                {log.error_message}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* 追蹤日誌 */}
      {activeTab === 4 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('notifications.tracking_logs_title')}</Typography>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={loadTrackingLogs}
                disabled={trackingLoading}
              >
                {t('notifications.refresh')}
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {trackingLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('notifications.col_event_type')}</TableCell>
                      <TableCell>{t('notifications.col_platform')}</TableCell>
                      <TableCell>{t('notifications.col_status')}</TableCell>
                      <TableCell>{t('notifications.col_order_id')}</TableCell>
                      <TableCell>{t('notifications.col_user_id')}</TableCell>
                      <TableCell>{t('notifications.col_ip_address')}</TableCell>
                      <TableCell>{t('notifications.col_sent_time')}</TableCell>
                      <TableCell>{t('notifications.col_error')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trackingLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          {t('notifications.no_tracking_logs')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      trackingLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Chip size="small" label={log.event_type} />
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={log.platform} color="primary" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={getStatusLabel(log.status)}
                              color={getStatusColor(log.status)}
                            />
                          </TableCell>
                          <TableCell>{log.order_id || '-'}</TableCell>
                          <TableCell>{log.user_id || '-'}</TableCell>
                          <TableCell>{log.ip_address || '-'}</TableCell>
                          <TableCell>
                            {new Date(log.created_at).toLocaleString('zh-TW')}
                          </TableCell>
                          <TableCell>
                            {log.error_message && (
                              <Typography variant="caption" color="error">
                                {log.error_message}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* 測試對話框 */}
      <Dialog open={testDialog.open} onClose={() => setTestDialog({ open: false, type: '', data: {} })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('notifications.test_dialog_title', { type: getTypeLabel(testDialog.type) })}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {testDialog.type === 'sms' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('notifications.phone_number')}
                    value={testDialog.data.phone || ''}
                    onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, phone: e.target.value } })}
                    placeholder="0912345678"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('notifications.sms_content')}
                    value={testDialog.data.message || ''}
                    onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, message: e.target.value } })}
                    required
                  />
                </Grid>
              </Grid>
            )}

            {testDialog.type === 'email' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="email"
                    label={t('notifications.recipient_email')}
                    value={testDialog.data.to || ''}
                    onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, to: e.target.value } })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('notifications.email_subject')}
                    value={testDialog.data.subject || ''}
                    onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, subject: e.target.value } })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label={t('notifications.email_content_html')}
                    value={testDialog.data.htmlContent || ''}
                    onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, htmlContent: e.target.value } })}
                  />
                </Grid>
              </Grid>
            )}

            {testDialog.type === 'line' && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('notifications.notification_content')}
                value={testDialog.data.message || ''}
                onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, message: e.target.value } })}
                required
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialog({ open: false, type: '', data: {} })}>
            {t('notifications.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleTest}
            disabled={testing}
            startIcon={<SendIcon />}
          >
            {testing ? t('notifications.sending') : t('notifications.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationSettings;
